const MIN_PASSWORD_LENGTH = 20;
const COMMON_PASSWORD_WORDS = [
  'passwort',
  'password',
  'admin',
  'stark',
  'qwerty',
  'letmein',
  'welcome',
];

export type PasswordPolicyCheck = {
  key: string;
  label: string;
  passed: boolean;
};

export type PasswordPolicyResult = {
  valid: boolean;
  checks: PasswordPolicyCheck[];
  errors: string[];
};

export function validatePasswordPolicy(password: string): PasswordPolicyResult {
  const chars = password.split('');

  const digitPositions = chars
    .map((ch, idx) => ({ ch, idx }))
    .filter(({ ch }) => /\d/.test(ch))
    .map(({ idx }) => idx);

  const specialPositions = chars
    .map((ch, idx) => ({ ch, idx }))
    .filter(({ ch }) => /[^A-Za-z0-9]/.test(ch))
    .map(({ idx }) => idx);

  const lowerPassword = password.toLowerCase();
  const hasCommonWord = COMMON_PASSWORD_WORDS.some((word) => lowerPassword.includes(word));

  const checks: PasswordPolicyCheck[] = [
    {
      key: 'length',
      label: `Mindestens ${MIN_PASSWORD_LENGTH} Zeichen`,
      passed: password.length >= MIN_PASSWORD_LENGTH,
    },
    {
      key: 'uppercase',
      label: 'Mindestens ein Grossbuchstabe',
      passed: /[A-Z]/.test(password),
    },
    {
      key: 'lowercase',
      label: 'Mindestens ein Kleinbuchstabe',
      passed: /[a-z]/.test(password),
    },
    {
      key: 'digit',
      label: 'Mindestens eine Ziffer',
      passed: /\d/.test(password),
    },
    {
      key: 'special',
      label: 'Mindestens ein Sonderzeichen',
      passed: /[^A-Za-z0-9]/.test(password),
    },
    {
      key: 'digit-not-tail',
      label: 'Ziffern dürfen nicht nur am Ende stehen',
      passed: digitPositions.length === 0 || !digitPositions.every((idx) => idx >= password.length - 3),
    },
    {
      key: 'special-not-tail',
      label: 'Sonderzeichen dürfen nicht nur am Ende stehen',
      passed: specialPositions.length === 0 || !specialPositions.every((idx) => idx >= password.length - 3),
    },
    {
      key: 'no-year-tail',
      label: 'Keine Jahreszahl am Ende erlaubt',
      passed: !/(19|20)\d{2}$/.test(password),
    },
    {
      key: 'no-repeat',
      label: 'Keine 3+ gleichen Zeichen hintereinander',
      passed: !/(.)\1{2,}/.test(password),
    },
    {
      key: 'no-common-words',
      label: 'Keine gängigen Wörter verwenden',
      passed: !hasCommonWord,
    },
  ];

  const errors = checks.filter((check) => !check.passed).map((check) => check.label);
  return {
    valid: errors.length === 0,
    checks,
    errors,
  };
}
